import { createGlobalStyle } from 'styled-components'

const AccessibilityStyles = createGlobalStyle`
  /* Reduce motion — strengthens prefers-reduced-motion by killing all CSS
     animations and transitions site-wide when the user opts in. */
  :root[data-reduced-motion="true"] *,
  :root[data-reduced-motion="true"] *::before,
  :root[data-reduced-motion="true"] *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }

  /* Grayscale — applied to html so fixed-position children like the
     ambience background are also desaturated. */
  :root[data-grayscale="true"] {
    filter: grayscale(1);
  }

  /* High contrast — overrides the theme color variables to pure B&W.
     Inverts based on active theme. */
  :root[data-high-contrast="true"] {
    --text-color: 0, 0, 0;
    --bg-color: 255, 255, 255;
    --caption-color: 0, 0, 0;
  }
  :root[data-high-contrast="true"][data-theme="dark"] {
    --text-color: 255, 255, 255;
    --bg-color: 0, 0, 0;
    --caption-color: 255, 255, 255;
  }
  @media (prefers-color-scheme: dark) {
    :root[data-high-contrast="true"]:not([data-theme]) {
      --text-color: 255, 255, 255;
      --bg-color: 0, 0, 0;
      --caption-color: 255, 255, 255;
    }
  }

  /* High contrast — force pure text color on all common text-bearing
     elements site-wide. Uses :is() to target element types that carry
     text (not layout divs, which would break hidden layers and
     transition states). Hover and aria-checked states are excluded so
     the brand pink accent still shows on interactive feedback.
     Note: opacity is NOT forced here — only inside the overlay (below)
     where it's used for text dimming. Forcing opacity site-wide would
     break show/hide animations (e.g. the hamburger icon crossfade). */
  :root[data-high-contrast="true"] :is(p, li, span, a, button, h1, h2, h3, h4, h5, h6, label, legend, figcaption, blockquote, dt, dd, summary, small, strong, em, b, i, u, s, mark, del, ins, sub, sup, abbr, cite, q, dfn, time, code, pre, var, samp, kbd):not(:hover):not([aria-checked='true']) {
    color: rgb(var(--text-color)) !important;
  }

  /* Force opacity: 1 only inside the visible overlay layer, where
     opacity is used for text dimming (not for show/hide states). */
  :root[data-high-contrast="true"] #nav-overlay [aria-hidden='false'] *:not(:hover):not([aria-checked='true']) {
    opacity: 1 !important;
  }

  /* Terminal overlay: the card bg is rgba(var(--text-color), 0.85), so
     forcing text to --text-color makes it invisible. Override terminal
     text to use --bg-color which contrasts against the card bg. */
  :root[data-high-contrast="true"] [data-terminal] :is(span, a, button, code, pre, label):not(:hover) {
    color: rgb(var(--bg-color)) !important;
  }

  /* Ambience sections + manual theme/high contrast:
     AmbiencePresence writes inline --text-color and --box-bg-opacity on
     the article element (marked [data-ambience-active]) based on image
     luminance. When the user has pinned a theme or enabled high contrast,
     those inline values shadow the page-level variables inside the
     article subtree, producing white-on-white or black-on-black text.
     Force the article to inherit these custom properties from :root
     instead, neutralizing the inline declarations. Stylesheet !important
     beats non-important inline, so this wins cleanly. */
  :root[data-user-theme] [data-ambience-active],
  :root[data-high-contrast="true"] [data-ambience-active] {
    --text-color: inherit !important;
    --box-bg-opacity: inherit !important;
  }

  :root[data-high-contrast="true"] input::placeholder,
  :root[data-high-contrast="true"] textarea::placeholder {
    color: rgb(var(--text-color)) !important;
    opacity: 0.7 !important;
  }

  :root[data-high-contrast="true"] hr,
  :root[data-high-contrast="true"] [role='separator'] {
    border-color: rgb(var(--text-color)) !important;
    background: rgb(var(--text-color)) !important;
    opacity: 0.5 !important;
  }

  /* Larger text — bumps root font size for easier reading. We override
     the clamp set by Body1Styles on html with a larger clamp so the
     increase is visible across the full responsive range. */
  :root[data-larger-text="true"] {
    font-size: clamp(20px, calc(100vw * 2.2 / 100), 23px);
  }

  /* Body1Styles applies a hardcoded px-based clamp to p and li in
     BlogStyles, which ignores the root font-size change above. Override
     those elements directly so blog article content also scales. */
  :root[data-larger-text="true"] p,
  :root[data-larger-text="true"] li {
    font-size: clamp(20px, calc(100vw * 2.2 / 100), 23px) !important;
  }

  /* Always underline links. */
  :root[data-underline-links="true"] a,
  :root[data-underline-links="true"] a:visited,
  :root[data-underline-links="true"] a:active {
    text-decoration: underline !important;
  }
`

export default AccessibilityStyles
